"use client";

import React, { useEffect, useState, useCallback } from "react";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import sal from "sal.js";
import Pricing from "@/components/Plan/Pricing";
import Loading from "@/components/Common/Loading";

/**
 * Componente Plans que muestra los planes de suscripción y la información de precios.
 * Se encarga de:
 * - Inicializar las animaciones con sal.js.
 * - Obtener los datos de suscripción si hay sesión activa.
 * - Manejar estados de carga y error.
 *
 * @returns {JSX.Element} - Renderizado de la sección de planes.
 */
const Plans = () => {
  // Manejo de la sesión y navegación
  const { data: session, status } = useSession();
  const router = useRouter();

  // Estados locales para controlar la carga, error y datos de suscripción
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [subscriptionFlowStatus, setSubscriptionFlowStatus] = useState("");

  // URL de la API obtenida de las variables de entorno
  const apiUrl = process.env.NEXT_PUBLIC_API_URL;

  // 1) Inicialización de sal.js al montar el componente
  useEffect(() => {
    sal(); // Inicia las animaciones de sal.js
  }, []);

  // 2) Función para obtener los datos de suscripción si hay sesión activa
  const fetchSubscription = useCallback(async () => {
    try {
      const response = await fetch(`${apiUrl}/user/get-subscription`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSubscriptionFlowStatus(data.subscription_flow_status);
    } catch (err) {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl, session?.token]);

  // 3) Si ocurre un error, redirige a la página de error
  useEffect(() => {
    if (hasError) {
      router.push("/error");
    }
  }, [hasError, router]);

  // 4) Cuando la sesión está lista, se realiza el fetch si hay sesión; de lo contrario, se detiene la carga
  useEffect(() => {
    if (status !== "loading") {
      if (session) {
        fetchSubscription();
      } else {
        setIsLoading(false);
      }
    }
  }, [status, session, fetchSubscription]);

  // 5) Re-inicializa sal.js cuando isLoading cambia a false, para detectar elementos ahora montados
  useEffect(() => {
    if (!isLoading) {
      sal();
    }
  }, [isLoading]);

  // 6) Renderizado condicional mientras se cargan los datos o la sesión
  if (isLoading || status === "loading") {
    return <Loading />;
  }

  // Determina si hay sesión activa
  const hasSession = Boolean(session);

  return (
    <div className="rainbow-pricing-area rainbow-section-gap">
      <div className="container-fluid">
        <div className="wrapper">
          <div className="container pl--0 pr--0">
            {/* Sección de título con animación de sal.js */}
            <div className="row">
              <div className="col-lg-12">
                <div
                  className="section-title text-center"
                  data-sal="slide-up"
                  data-sal-duration="400"
                  data-sal-delay="150"
                >
                  <h2 className="subtitle">
                    <span className="theme-gradient">Planes</span>
                  </h2>
                  <h2 className="title w-600 mb--20">
                    Elige cómo quieres crear tus clases
                  </h2>
                  <p className="description b1">
                    Empieza gratis o pasa a un plan ilimitado para crear planes
                    de lección, evaluaciones y presentaciones en minutos.
                  </p>
                </div>
              </div>
            </div>

            {/* Componente Pricing con los datos de suscripción */}
            <Pricing
              parentClass="col-xl-4 col-lg-6 col-md-6 col-12 mt--30"
              hasSession={hasSession}
              subscriptionFlowStatus={subscriptionFlowStatus}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Plans;
